import { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import {
  DEFAULT_MODIFIERS, DEFAULT_PLANT_DENSITY, getSuggestedServices,
  getAreaTypeLabel, hasPreEmergentService, withoutPreEmergentService,
} from '../../SiteTakeoff/siteTakeoffConstants';

const MAX_UNDO_DEPTH = 25;

const getAreaIdentity = (area, index) => area?._id || area?.id || area?.clientId || `legacy_${index}`;

const getLegacyIgnoreStorageKey = (jobsiteId) => `siteTakeoffV3.ignoredLegacyAreas.${jobsiteId}`;

const readIgnoredLegacyAreaIds = (jobsiteId) => {
  if (!jobsiteId) return [];
  try {
    const raw = localStorage.getItem(getLegacyIgnoreStorageKey(jobsiteId));
    const parsed = JSON.parse(raw || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
};

const writeIgnoredLegacyAreaIds = (jobsiteId, ids) => {
  if (!jobsiteId) return;
  try {
    localStorage.setItem(
      getLegacyIgnoreStorageKey(jobsiteId),
      JSON.stringify(Array.from(new Set(ids)).filter(Boolean))
    );
  } catch (_) {}
};

/**
 * Loads takeoffs for a jobsite and manages the currently-active takeoff's areas.
 * Returns areas + CRUD callbacks + takeoff list + active takeoff metadata.
 *
 * Fetches admin-configured service defaults per entity and uses those when
 * assigning services to new areas. Falls back to hardcoded defaults if none configured.
 */
export default function useAreas(url, jobsite) {
  const [areas, setAreas] = useState([]);
  const [takeoffs, setTakeoffs] = useState([]);
  const [takeoff, setTakeoff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [undoStack, setUndoStack] = useState([]);
  const [ignoredLegacyAreaIds, setIgnoredLegacyAreaIds] = useState([]);
  const initialLoadRef = useRef(false);
  const customDefaultsRef = useRef(null); // admin-configured defaults
  const areasRef = useRef([]);
  const takeoffRef = useRef(null);

  useEffect(() => { areasRef.current = areas; }, [areas]);
  useEffect(() => { takeoffRef.current = takeoff; }, [takeoff]);

  // Fetch admin-configured service defaults
  useEffect(() => {
    if (!url) return;
    axios.get(`${url}/api/customer/area-type-defaults`)
      .then(resp => {
        const d = resp.data?.defaults;
        customDefaultsRef.current = (d && Object.keys(d).length > 0) ? d : null;
      })
      .catch(() => { customDefaultsRef.current = null; });
  }, [url]);

  useEffect(() => {
    const handleDefaultsUpdated = (event) => {
      const d = event?.detail?.defaults;
      customDefaultsRef.current = (d && Object.keys(d).length > 0) ? d : null;
    };
    window.addEventListener('siteTakeoff:areaTypeDefaultsUpdated', handleDefaultsUpdated);
    return () => window.removeEventListener('siteTakeoff:areaTypeDefaultsUpdated', handleDefaultsUpdated);
  }, []);

  // Get suggested services using admin overrides if available
  const getServices = useCallback((areaType) => {
    if (customDefaultsRef.current && customDefaultsRef.current[areaType]) {
      return customDefaultsRef.current[areaType];
    }
    return getSuggestedServices(areaType);
  }, []);

  const normalizeAreas = (rawAreas) =>
    (rawAreas || [])
      .filter(a => Array.isArray(a.paths) && a.paths.length > 0 && 'lat' in a.paths[0])
      .map(a => {
        const rawServices = a.servicesRequired?.length ? a.servicesRequired : getServices(a.areaType);
        return {
          ...a,
          id: a.id || a._id,
          plantDensity: a.plantDensity || { ...DEFAULT_PLANT_DENSITY },
          modifiers: a.modifiers || { ...DEFAULT_MODIFIERS },
          servicesRequired: withoutPreEmergentService(rawServices),
          preEmergentEnabled: typeof a.preEmergentEnabled === 'boolean'
            ? a.preEmergentEnabled
            : hasPreEmergentService(rawServices),
          notes: a.notes || '',
          isNew: false,
        };
      });

  const fetchTakeoffList = useCallback(async (jobsiteId) => {
    if (!url || !jobsiteId) return [];
    try {
      const resp = await axios.get(`${url}/api/customer/site-takeoffs?jobSiteId=${jobsiteId}`);
      const list = resp.data?.takeoffs || [];
      setTakeoffs(list);
      return list;
    } catch (err) {
      console.error('fetchTakeoffList error:', err);
      setTakeoffs([]);
      return [];
    }
  }, [url]);

  const loadTakeoffById = useCallback(async (takeoffId) => {
    if (!url || !takeoffId) return null;
    setLoading(true);
    initialLoadRef.current = true;
    try {
      const resp = await axios.get(`${url}/api/customer/site-takeoff/${takeoffId}`);
      const { success, takeoff: t } = resp.data || {};
      if (success && t) {
        setAreas(normalizeAreas(t.areas));
        setTakeoff({
          _id: t._id,
          name: t.name || 'Site Takeoff',
          overlays: t.overlays || [],
          placementQueue: t.placementQueue || [],
          productionRates: t.productionRates || {},
        });
        setDirty(false);
        setUndoStack([]);
        return t;
      }
    } catch (err) {
      console.error('loadTakeoffById error:', err);
    } finally {
      setLoading(false);
      setTimeout(() => { initialLoadRef.current = false; }, 0);
    }
    return null;
  }, [url]);

  useEffect(() => {
    if (!url || !jobsite?._id) {
      setAreas([]);
      setTakeoff(null);
      setTakeoffs([]);
      setDirty(false);
      setUndoStack([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const list = await fetchTakeoffList(jobsite._id);
      if (cancelled) return;
      if (list.length > 0) {
        await loadTakeoffById(list[0]._id);
      } else {
        setAreas([]);
        setTakeoff(null);
        setDirty(false);
        setUndoStack([]);
      }
    })();
    return () => { cancelled = true; };
  }, [url, jobsite, fetchTakeoffList, loadTakeoffById]);

  useEffect(() => {
    if (initialLoadRef.current) return;
    if (areas.length === 0 && !takeoff?._id) return;
    setDirty(true);
  }, [areas, takeoff]);

  useEffect(() => {
    if (!jobsite?._id) {
      setIgnoredLegacyAreaIds([]);
      return;
    }
    setIgnoredLegacyAreaIds(readIgnoredLegacyAreaIds(jobsite._id));
  }, [jobsite?._id]);

  const pushUndoSnapshot = useCallback((label = 'Change') => {
    const snapshot = {
      label,
      areas: areasRef.current,
      takeoff: takeoffRef.current,
    };
    setUndoStack(prev => [...prev.slice(-(MAX_UNDO_DEPTH - 1)), snapshot]);
  }, []);

  const undoLastChange = useCallback(() => {
    const snapshot = undoStack[undoStack.length - 1];
    if (!snapshot) return false;
    setUndoStack(prev => prev.slice(0, -1));
    initialLoadRef.current = true;
    setAreas(snapshot.areas || []);
    setTakeoff(snapshot.takeoff || null);
    setDirty(true);
    setTimeout(() => { initialLoadRef.current = false; }, 0);
    return true;
  }, [undoStack]);

  const addArea = useCallback((area, options = {}) => {
    if (!options.skipUndo) pushUndoSnapshot('Add area');
    const rawServices = area.servicesRequired?.length ? area.servicesRequired : getServices(area.areaType);
    const servicesRequired = withoutPreEmergentService(rawServices);
    const newArea = {
      id: uuidv4(),
      isNew: true,
      plantDensity: { ...DEFAULT_PLANT_DENSITY },
      modifiers: { ...DEFAULT_MODIFIERS },
      notes: '',
      ...area,
      servicesRequired,
      preEmergentEnabled: typeof area.preEmergentEnabled === 'boolean'
        ? area.preEmergentEnabled
        : hasPreEmergentService(rawServices),
    };
    setAreas(prev => [...prev, newArea]);
    return newArea;
  }, [getServices, pushUndoSnapshot]);

  const updateArea = useCallback((areaId, updates) => {
    setAreas(prev => prev.map(a => {
      if ((a._id || a.id) !== areaId) return a;
      const merged = { ...a };
      Object.entries(updates).forEach(([k, v]) => {
        if (k === 'plantDensity' && typeof v === 'object') {
          merged.plantDensity = { ...(a.plantDensity || DEFAULT_PLANT_DENSITY), ...v };
        } else if (k === 'modifiers' && typeof v === 'object') {
          merged.modifiers = { ...(a.modifiers || DEFAULT_MODIFIERS), ...v };
        } else if (k === 'treeDetails' && typeof v === 'object') {
          merged.treeDetails = { ...(a.treeDetails || {}), ...v };
        } else {
          merged[k] = v;
        }
      });
      return merged;
    }));
  }, []);

  // Delete an area — cascade-delete child exclusions too
  const deleteArea = useCallback((areaId) => {
    pushUndoSnapshot('Delete area');
    setAreas(prev => prev.filter(a => {
      const id = a._id || a.id;
      if (id === areaId) return false;
      // Also remove child exclusions of the deleted area
      const pid = a.parentAreaId;
      if (pid && (pid === areaId || pid.toString?.() === areaId)) return false;
      return true;
    }));
  }, [pushUndoSnapshot]);

  const duplicateArea = useCallback((areaId, groupOptions) => {
    const source = areasRef.current.find(a => (a._id || a.id) === areaId);
    if (!source) return null;

    pushUndoSnapshot('Duplicate area');
    const newId = uuidv4();

    setAreas(prev => {
      const latestSource = prev.find(a => (a._id || a.id) === areaId) || source;

      // Offset paths slightly so the copy isn't stacked directly on top
      const offsetLat = 0.0003;
      const offsetLng = 0.0004;
      const offsetPaths = (latestSource.paths || []).map(p => ({
        lat: p.lat + offsetLat,
        lng: p.lng + offsetLng,
      }));

      const keepExclusion = groupOptions?.keepExclusion && latestSource.parentAreaId;
      const copy = {
        ...latestSource,
        _id: undefined,
        id: newId,
        isNew: true,
        name: `${latestSource.name || 'Area'} (copy)`,
        paths: offsetPaths,
        parentAreaId: keepExclusion ? latestSource.parentAreaId : null,
        exclusionType: keepExclusion ? latestSource.exclusionType : null,
      };

      // Apply group if provided
      if (groupOptions?.groupId) {
        copy.groupId = groupOptions.groupId;
        copy.groupName = groupOptions.groupName || '';
        // Also tag the source if it doesn't have this group yet
        const updatedPrev = prev.map(a => {
          if ((a._id || a.id) === areaId && !a.groupId) {
            return { ...a, groupId: groupOptions.groupId, groupName: groupOptions.groupName || '' };
          }
          return a;
        });
        return [...updatedPrev, copy];
      }

      return [...prev, copy];
    });
    return newId;
  }, [pushUndoSnapshot]);

  const groupAreas = useCallback((areaIds, groupName) => {
    const ids = new Set(areaIds || []);
    if (ids.size < 2) return null;
    pushUndoSnapshot('Group areas');
    const groupId = uuidv4();
    const resolvedName = groupName || 'Group';
    setAreas(prev => prev.map(a => (ids.has(a._id || a.id)
      ? { ...a, groupId, groupName: resolvedName }
      : a)));
    return groupId;
  }, [pushUndoSnapshot]);

  const ungroupArea = useCallback((areaId) => {
    pushUndoSnapshot('Ungroup area');
    setAreas(prev => prev.map(a => ((a._id || a.id) === areaId
      ? { ...a, groupId: null, groupName: null }
      : a)));
  }, [pushUndoSnapshot]);

  const ungroupAll = useCallback((groupId) => {
    if (!groupId) return;
    pushUndoSnapshot('Ungroup areas');
    setAreas(prev => prev.map(a => (a.groupId === groupId
      ? { ...a, groupId: null, groupName: null }
      : a)));
  }, [pushUndoSnapshot]);

  const replaceAreaWithAreas = useCallback((areaId, replacementAreas = [], label = 'Split area') => {
    const cleanReplacements = replacementAreas.filter(a => a?.paths?.length);
    if (!areaId || cleanReplacements.length === 0) return [];
    const sourceArea = areasRef.current.find(a => (a._id || a.id) === areaId);
    if (!sourceArea) return [];
    pushUndoSnapshot(label);
    const newIds = cleanReplacements.map((area, idx) => (
      idx === 0 ? (sourceArea.id || sourceArea._id) : (area.id || uuidv4())
    ));
    setAreas(prev => {
      const source = prev.find(a => (a._id || a.id) === areaId);
      if (!source) return prev;
      const replacements = cleanReplacements.map((area, idx) => {
        const resolvedId = newIds[idx];
        return {
          ...source,
          ...area,
          _id: idx === 0 ? source._id : undefined,
          id: resolvedId,
          isNew: idx === 0 ? source.isNew : true,
        };
      });
      return prev.reduce((acc, area) => {
        if ((area._id || area.id) === areaId) return [...acc, ...replacements];
        return [...acc, area];
      }, []);
    });
    return newIds;
  }, [pushUndoSnapshot]);

  const updatePlacementQueue = useCallback((nextQueueOrUpdater, label = 'Update queue') => {
    pushUndoSnapshot(label);
    setTakeoff(prev => {
      const current = prev || {
        _id: null,
        name: 'New Site Takeoff',
        overlays: [],
        placementQueue: [],
        productionRates: {},
      };
      const prevQueue = current.placementQueue || [];
      const nextQueue = typeof nextQueueOrUpdater === 'function'
        ? nextQueueOrUpdater(prevQueue)
        : nextQueueOrUpdater;
      return {
        ...current,
        placementQueue: Array.isArray(nextQueue) ? nextQueue : prevQueue,
      };
    });
    setDirty(true);
  }, [pushUndoSnapshot]);

  // saveTakeoff(takeoffName, extras) where extras = { overlays?, placementQueue? }
  // Extras come from external hooks (useOverlays, etc) and override the values
  // cached on the takeoff state — so the latest in-memory geometry wins.
  const saveTakeoff = useCallback(async (takeoffName, extras = {}) => {
    if (!url || !jobsite?._id) return null;
    setSaving(true);
    try {
      const areasToSave = areas.map(a => ({
        _id: a._id || undefined,
        clientId: a.id || undefined,
        name: a.name || getAreaTypeLabel(a.areaType),
        areaType: a.areaType,
        geometryType: a.geometryType || 'polygon',
        paths: a.paths,
        area: a.area,
        areaAcres: a.areaAcres,
        perimeter: a.perimeter,
        length: a.length,
        width: a.width,
        radiusX: a.radiusX,
        radiusY: a.radiusY,
        plantDensity: a.plantDensity,
        modifiers: a.modifiers,
        servicesRequired: withoutPreEmergentService(a.servicesRequired || []),
        preEmergentEnabled: typeof a.preEmergentEnabled === 'boolean'
          ? a.preEmergentEnabled
          : hasPreEmergentService(a.servicesRequired || []),
        notes: a.notes || '',
        treeDetails: a.treeDetails,
        groupId: a.groupId,
        groupName: a.groupName,
        fillColor: a.fillColor,
        strokeColor: a.strokeColor,
        parentAreaId: a.parentAreaId || null,
        exclusionType: a.exclusionType || null,
      }));

      const resolvedOverlays = Array.isArray(extras?.overlays)
        ? extras.overlays
        : (takeoff?.overlays || []);
      const resolvedQueue = Array.isArray(extras?.placementQueue)
        ? extras.placementQueue
        : (takeoff?.placementQueue || []);

      const resp = await axios.post(`${url}/api/hmi/genericPostRequest?type=SAVE_SITE_TAKEOFF`, {
        takeoffId: takeoff?._id || undefined,
        takeoffName: takeoffName || takeoff?.name || 'Site Takeoff',
        jobSite: { _id: jobsite._id },
        areas: areasToSave,
        overlays: resolvedOverlays,
        placementQueue: resolvedQueue,
        productionRates: takeoff?.productionRates || undefined,
      });

      if (resp.data?.areaErrors?.length > 0) {
        return {
          ...(resp.data.takeoff || {}),
          saveFailed: true,
          areaErrors: resp.data.areaErrors,
        };
      }

      if (resp.data?.success && resp.data?.takeoff?._id) {
        initialLoadRef.current = true;
        const savedId = resp.data.takeoff._id;
        const savedName = resp.data.takeoff.name || takeoffName;
        setTakeoff(prev => ({
          ...(prev || {}),
          _id: savedId,
          name: savedName,
          overlays: resolvedOverlays,
          placementQueue: resolvedQueue,
        }));
        const serverIds = resp.data.takeoff.areaIds || [];
        // Prefer the server's clientId→serverId map — robust under partial
        // failure, where areaIds is shorter than the submitted list and
        // positional mapping would hand areas the wrong ids. Fall back to
        // positional mapping only when every area saved (older responses).
        const lengthsMatch = serverIds.length === areasToSave.length;
        const clientIdToServerId = { ...(resp.data.clientIdMap || {}) };
        if (!resp.data.clientIdMap && lengthsMatch) {
          areasToSave.forEach((a, i) => {
            if (a.clientId && serverIds[i]) {
              clientIdToServerId[a.clientId] = serverIds[i];
            }
          });
        }
        if (serverIds.length > 0) {
          setAreas(prev => prev.map((a, i) => {
            const serverId = clientIdToServerId[a.id] || (lengthsMatch ? serverIds[i] : undefined);
            // Remap parentAreaId if it is a client UUID (not a 24-hex ObjectId)
            const rawParent = a.parentAreaId;
            const remappedParent = rawParent &&
              typeof rawParent === 'string' &&
              !/^[a-f0-9]{24}$/i.test(rawParent)
              ? (clientIdToServerId[rawParent] || rawParent)
              : rawParent;
            return {
              ...a,
              // Failed areas keep isNew/_id so the next save retries them.
              ...(serverId ? { _id: serverId, isNew: false } : {}),
              parentAreaId: remappedParent || a.parentAreaId,
            };
          }));
        }
        setDirty(false);
        setUndoStack([]);
        setTimeout(() => { initialLoadRef.current = false; }, 0);
        await fetchTakeoffList(jobsite._id);
        return resp.data.takeoff;
      }
    } catch (err) {
      console.error('saveTakeoff error:', err);
    } finally {
      setSaving(false);
    }
    return null;
  }, [url, jobsite, areas, takeoff, fetchTakeoffList]);

  const newTakeoff = useCallback(() => {
    initialLoadRef.current = true;
    setAreas([]);
    setTakeoff({
      _id: null,
      name: 'New Site Takeoff',
      overlays: [],
      placementQueue: [],
      productionRates: {},
    });
    setDirty(false);
    setUndoStack([]);
    setTimeout(() => { initialLoadRef.current = false; }, 0);
  }, []);

  const switchTakeoff = useCallback(async (takeoffId) => {
    if (!takeoffId || takeoffId === takeoff?._id) return;
    await loadTakeoffById(takeoffId);
  }, [takeoff, loadTakeoffById]);

  const deleteTakeoff = useCallback(async (takeoffId) => {
    if (!url || !takeoffId) return false;
    try {
      await axios.delete(`${url}/api/customer/site-takeoff/${takeoffId}`);
      const list = await fetchTakeoffList(jobsite?._id);
      if (takeoff?._id === takeoffId) {
        if (list.length > 0) {
          await loadTakeoffById(list[0]._id);
        } else {
          setAreas([]);
          setTakeoff(null);
          setDirty(false);
          setUndoStack([]);
        }
      }
      return true;
    } catch (err) {
      console.error('deleteTakeoff error:', err);
      return false;
    }
  }, [url, jobsite, takeoff, fetchTakeoffList, loadTakeoffById]);

  const renameTakeoff = useCallback((newName) => {
    setTakeoff(prev => prev ? { ...prev, name: newName } : prev);
    setDirty(true);
  }, []);

  // Update production rate overrides on the active takeoff. Marks dirty
  // so the user has to save to persist them.
  const updateProductionRates = useCallback((newRates) => {
    setTakeoff(prev => prev ? { ...prev, productionRates: newRates || {} } : prev);
    setDirty(true);
  }, []);

  // Import legacy JobSite.areas as a fresh new takeoff (creates new Area records)
  const importLegacyAreas = useCallback(async (legacyAreas, name = 'Imported Areas') => {
    if (!url || !jobsite?._id || !legacyAreas?.length) return null;
    setSaving(true);
    try {
      // Strip _id so the server creates new Area records instead of mutating the originals
      const areasToSave = legacyAreas
        .filter(a => Array.isArray(a.paths) && a.paths.length > 0 && 'lat' in a.paths[0])
        .map(a => ({
          name: a.name || getAreaTypeLabel(a.areaType),
          areaType: a.areaType,
          geometryType: a.geometryType || 'polygon',
          paths: a.paths,
          area: a.area,
          areaAcres: a.areaAcres,
          perimeter: a.perimeter,
          length: a.length || '0',
          width: a.width || 0,
          radiusX: a.radiusX || 0,
          radiusY: a.radiusY || 0,
          plantDensity: a.plantDensity || { ...DEFAULT_PLANT_DENSITY },
          modifiers: a.modifiers || { ...DEFAULT_MODIFIERS },
          servicesRequired: withoutPreEmergentService(
            a.servicesRequired?.length
              ? a.servicesRequired
              : getServices(a.areaType)
          ),
          preEmergentEnabled: typeof a.preEmergentEnabled === 'boolean'
            ? a.preEmergentEnabled
            : hasPreEmergentService(a.servicesRequired?.length ? a.servicesRequired : getServices(a.areaType)),
          notes: a.notes || '',
          treeDetails: a.treeDetails,
          groupId: a.groupId,
          groupName: a.groupName,
          fillColor: a.fillColor,
          strokeColor: a.strokeColor || '#000000',
        }));

      if (areasToSave.length === 0) return null;

      const resp = await axios.post(
        `${url}/api/hmi/genericPostRequest?type=SAVE_SITE_TAKEOFF`,
        {
          takeoffName: name,
          jobSite: { _id: jobsite._id },
          areas: areasToSave,
        }
      );

      if (resp.data?.success && resp.data?.takeoff?._id) {
        // Reload list + switch to the new takeoff
        await fetchTakeoffList(jobsite._id);
        await loadTakeoffById(resp.data.takeoff._id);
        return resp.data.takeoff;
      }
    } catch (err) {
      console.error('importLegacyAreas error:', err);
    } finally {
      setSaving(false);
    }
    return null;
  }, [url, jobsite, fetchTakeoffList, loadTakeoffById]);

  const ignoreLegacyAreas = useCallback((areaIdentities) => {
    if (!jobsite?._id) return;
    const next = Array.from(new Set([
      ...ignoredLegacyAreaIds,
      ...(areaIdentities || []),
    ]));
    writeIgnoredLegacyAreaIds(jobsite._id, next);
    setIgnoredLegacyAreaIds(next);
  }, [jobsite?._id, ignoredLegacyAreaIds]);

  const restoreIgnoredLegacyAreas = useCallback(() => {
    if (!jobsite?._id) return;
    writeIgnoredLegacyAreaIds(jobsite._id, []);
    setIgnoredLegacyAreaIds([]);
  }, [jobsite?._id]);

  const ignoredSet = new Set(ignoredLegacyAreaIds);
  const legacyImportCandidates = (jobsite?.areas || [])
    .map((area, index) => ({
      ...area,
      importId: getAreaIdentity(area, index),
    }))
    .filter(area => !ignoredSet.has(area.importId));

  return {
    areas,
    takeoffs,
    takeoff,
    loading,
    saving,
    dirty,
    canUndo: undoStack.length > 0,
    undoLabel: undoStack[undoStack.length - 1]?.label || '',
    addArea,
    updateArea,
    deleteArea,
    duplicateArea,
    groupAreas,
    ungroupArea,
    ungroupAll,
    replaceAreaWithAreas,
    pushUndoSnapshot,
    undoLastChange,
    saveTakeoff,
    newTakeoff,
    switchTakeoff,
    deleteTakeoff,
    renameTakeoff,
    updateProductionRates,
    importLegacyAreas,
    legacyImportCandidates,
    ignoredLegacyAreaIds,
    ignoreLegacyAreas,
    restoreIgnoredLegacyAreas,
    updatePlacementQueue,
  };
}
